import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useBusinessTypeFeatures } from "@/hooks/useBusinessTypeFeatures";

interface ResetDataDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ResetDataDialog = ({ open, onOpenChange }: ResetDataDialogProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { businessTypeFeatures } = useBusinessTypeFeatures();
  const hasPoultryFeature = businessTypeFeatures && 
    typeof businessTypeFeatures === 'object' && 
    'poultry' in businessTypeFeatures && 
    businessTypeFeatures.poultry === true;

  const handleReset = async () => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Vous devez être connecté pour effectuer cette action");
        return;
      }

      // Delete all cart items
      await supabase
        .from('cart_items')
        .delete()
        .eq('created_by', user.id);

      // Delete all stock losses
      await supabase
        .from('stock_losses')
        .delete()
        .eq('created_by', user.id);

      // Delete all counting history and discrepancies
      await supabase
        .from('counting_history')
        .delete()
        .eq('owner_id', user.id);

      await supabase
        .from('counting_discrepancies')
        .delete()
        .eq('created_by', user.id);

      // Delete all validated amounts
      await supabase
        .from('validated_amounts')
        .delete()
        .eq('owner_id', user.id);

      // Reset access controls
      await supabase
        .from('access_controls')
        .delete()
        .eq('owner_id', user.id);

      // If poultry feature is enabled, delete poultry-specific data
      if (hasPoultryFeature) {
        // Delete egg productions
        await supabase
          .from('egg_productions')
          .delete()
          .eq('created_by', user.id);

        // Delete breeding cycles
        await supabase
          .from('poultry_breeding_cycles')
          .delete()
          .eq('created_by', user.id);

        // Get all transactions to delete credit payments first
        const { data: transactions } = await supabase
          .from('poultry_transactions')
          .select('id')
          .eq('created_by', user.id);

        if (transactions && transactions.length > 0) {
          const transactionIds = transactions.map(t => t.id);
          
          // Delete credit payments associated with these transactions
          await supabase
            .from('credit_payments')
            .delete()
            .in('transaction_id', transactionIds);
        }

        // Delete poultry transactions
        await supabase
          .from('poultry_transactions')
          .delete()
          .eq('created_by', user.id);
      }

      // Delete all stock items
      const { data: ownerData } = await supabase
        .from('owner_data')
        .select('business_type_id')
        .eq('owner_id', user.id)
        .single();

      if (ownerData) {
        const { data: businessStock } = await supabase
          .from('business_stocks')
          .select('id')
          .eq('business_id', ownerData.business_type_id)
          .single();

        if (businessStock) {
          await supabase
            .from('stock_items')
            .delete()
            .eq('stock_id', businessStock.id);
        }
      }

      toast.success("Toutes les données ont été réinitialisées avec succès");
      onOpenChange(false);
    } catch (error) {
      console.error("Erreur lors de la réinitialisation des données:", error);
      toast.error("Une erreur est survenue lors de la réinitialisation des données");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Réinitialiser les données</DialogTitle>
          <DialogDescription>
            Êtes-vous sûr de vouloir réinitialiser toutes les données ? Cette action supprimera définitivement :
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Tous les produits en stock</li>
              <li>Toutes les transactions</li>
              <li>Toutes les pertes déclarées</li>
              <li>Tout l'historique de comptage</li>
              <li>Toutes les différences de comptage</li>
              <li>Tous les codes d'accès aux onglets</li>
              {hasPoultryFeature && (
                <>
                  <li>Toutes les productions d'œufs</li>
                  <li>Tous les cycles de reproduction</li>
                </>
              )}
              <li>Tous les articles du panier</li>
            </ul>
            Cette action est irréversible.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button
            variant="destructive"
            onClick={handleReset}
            disabled={isLoading}
          >
            {isLoading ? "Réinitialisation..." : "Réinitialiser"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};